<template>
  <div v-show="!hideLogin">
    <UnauthorizedBlock>
      <div class="flex items-end mb-14">
        <div class="text-xl font-bold text-text leading-tight">
          {{ $t('title') }}
        </div>
        <router-link class="text-2sm text-text-label leading-tight ml-auto
                          transition duration-200 hover:text-red"
                     :to="{name:'register'}">
          {{ $t('regLink') }}
        </router-link>
      </div>
      <div v-if="!enabledAuth" class="text-md font-medium text-text leading-tight">
        {{ $t('authDisabledText') }}
      </div>
      <form v-else @submit.prevent=""
            class="grid grid-cols-1 row-gap-8">
        <BaseInput v-model.trim="$v.username.$model"
                   :label="$t('usernameLabel')"
                   :class="{'--error':$v.username.$error || !!serverErrors.username}">
          <template v-slot:errors>
            <div class="error" v-if="!$v.username.required">{{ $t('errors.required') }}</div>
            <div class="error" v-if="!$v.username.required && !!serverErrors.username">{{ serverErrors.username[0] }}
            </div>
          </template>
        </BaseInput>
        <div>
          <PasswordInput v-model.trim="$v.password.$model"
                         :label="$t('passwordLabel')"
                         type="password"
                         :password-reveal="true"
                         :error="$v.password.$error || !!serverErrors.password">
            <template v-slot:errors>
              <div class="error" v-if="!$v.password.required">{{ $t('errors.required') }}</div>
              <div class="error" v-else-if="!!serverErrors.password">{{ serverErrors.password }}
              </div>
            </template>
          </PasswordInput>
          <router-link class="mt-4 pl-7 flex
                            text-2sm text-text-label leading-tight ml-auto
                            transition duration-200 hover:text-red"
                       :to="{name:'restore'}">{{ $t('forgotPass') }}
          </router-link>
        </div>
        <BaseButton theme="red"
                    class="w-full"
                    :loading="submitted"
                    @click="loginBtn">
          {{ $t('loginBtn') }}
        </BaseButton>
        <LoginOAuth/>
      </form>
    </UnauthorizedBlock>
    <RecaptchaBranding/>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {required} from 'vuelidate/lib/validators'
import UnauthorizedBlock from "@/components/layouts/common/UnauthorizedBlock";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import BaseButton from "@/components/UI/BaseButton";
import PasswordInput from "@/components/UI/BaseInput/PasswordInput";
import RecaptchaBranding from "@/components/common/RecaptchaBranding";
import LoginOAuth from "@/views/guests/Login/LoginOAuth";

export default {
  name: 'home',
  components: {
    LoginOAuth,
    RecaptchaBranding,
    PasswordInput,
    BaseButton,
    BaseInput,
    UnauthorizedBlock,
  },
  data() {
    return {
      hideLogin: false,
      enabledAuth: true,
      username: "",
      password: "",
      submitted: false,
      serverErrors: {
        error: '',
        username: '',
        password: '',
      },
    }
  },
  validations: {
    username: {
      required,
    },
    password: {
      required,
    }
  },
  computed: {
    ...mapState('auth', ['fp']),
  },
  methods: {
    ...mapMutations('auth', ['setRefreshToken', 'setAccessToken']),
    ...mapActions('auth', ['getStatus', 'login']),
    showErrors(messages) {
      if (typeof messages === 'string') {
        this.serverErrors.error = messages;
      }
      if (!!messages.recaptcha_response) {
        this.$toasted.error(this.$t('recaptchaError'))
      }
      !!messages.username ? this.serverErrors.username = messages.username : '';
      !!messages.password ? this.serverErrors.password = messages.password : '';
    },
    clearErrors() {
      this.serverErrors = {
        error: '',
        username: '',
        password: '',
      };
    },
    async loginBtn() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        await this.loginUser({username: this.username, password: this.password})
        this.submitted = false;
      } else {
        this.submitted = false;
      }
    },
    async loginDemoAcc() {
      //clear query
      let query = Object.assign({}, this.$route.query);
      delete query.demo;
      await this.$router.replace({query});

      //login demo
      await this.loginUser({username: 'demo', password: 'demo'});
    },
    async loginUser(userData) {
      try {
        let loginData = {...userData}
        
        await this.$recaptchaLoaded();
        loginData.recaptcha_response = await this.$recaptcha('login');
        loginData.fingerprint = this.fp.result;

        const res = await this.login(loginData);
        if (res.data.success) {
          const a_token = res.body.data.access_token,
              r_token = res.body.data.refresh_token;
          this.setRefreshToken(r_token)
          this.setAccessToken(a_token)
          this.$router.push({name: 'home'});
        } else {
          this.showErrors(res.body.message)
          setTimeout(() => {
            this.clearErrors()
          }, 5000);
        }
      } catch (e) {
        console.log(e)
      }
    }
  },
  async created() {
    if (this.$route.query.demo === '1') {
      this.hideLogin = true;
      this.loginDemoAcc();
    }
    const res = await this.getStatus();
    this.enabledAuth = res.body.data.login;
    if (this.$route.query.account_deleted === '1') {
      let query = Object.assign({}, this.$route.query);
      delete query.account_deleted;
      await this.$router.replace({query});
      this.$toasted.success(this.$t('accountDeleted'));
    }
    const oauthFailed = this.$route.query.oauth === 'error' || this.$route.query.oAuth === 'error';
    if (oauthFailed) {
      let query = Object.assign({}, this.$route.query);
      delete query.oauth;
      delete query.oAuth;
      await this.$router.replace({query});
      this.$toasted.error('Авторизация не удалась')
    }
  },
  i18n: {
    messages: {
      en: {
        title: 'Sign In',
        regLink: 'Haven\'t signed yet?',
        authDisabledText: 'Authorization currently is disabled',
        usernameLabel: 'E-mail or login',
        passwordLabel: 'Password',
        forgotPass: 'Forgot password?',
        loginBtn: 'Login',
        recaptchaError: 'Recaptcha validation error. Please reload page and try to login again',
        accountDeleted: 'Your account has been deleted'
      },
      ru: {
        title: 'Авторизация',
        regLink: 'Ещё не зарегистрированы?',
        authDisabledText: 'Авторизация пользователей временно ограничена',
        usernameLabel: 'E-mail или логин',
        passwordLabel: 'Пароль',
        forgotPass: 'Забыли пароль?',
        loginBtn: 'Войти',
        accountDeleted: 'Аккаунт удалён',
        recaptchaError: 'Ошибка валидации recaptcha. Пожалуйста обновите страницу и попровуйте снова'
      }
    }
  }
}
</script>
